import React from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'

import { LogComment } from '../../types/logComment'
import { get, post, patch, remove } from '../../utils/service'
import { toReadableTimestamp } from '../../utils/dateUtils'
import Alert from '../alert/alert'
import { User } from '../../types/user'
import ConfirmationDialog from '../dialog/confirmationDialog'
import CommentEditor from './commentEditor'

interface CommentsProps {
  logUrl: string
  logId: number
  user: User | null
}

const Comments = (props: CommentsProps): JSX.Element => {
  const { t } = useTranslation()
  const [comments, setComments] = React.useState<Array<LogComment>>([])
  const [commentText, setCommentText] = React.useState<string>('')
  const [isLoadingComments, setIsLoadingComments] = React.useState<boolean>(false)
  const [hasLoadCommentsError, setHasLoadCommentsError] = React.useState<boolean>(false)
  const [isSavingComment, setIsSavingComment] = React.useState<boolean>(false)
  const [hasSaveCommentError, setHasSaveCommentError] = React.useState<boolean>(false)

  const [saveDeleteCommentUrl, setSaveDeleteCommentUrl] = React.useState<string>('')
  const [deleteCommentErrorUrl, setDeleteCommentErrorUrl] = React.useState<string>('')
  const [deleteConfirmationCommentUrl, setDeleteConfirmationCommentUrl] = React.useState<string>('')

  const [saveEditCommentUrl, setSaveEditCommentUrl] = React.useState<string>('')
  const [editCommentErrorUrl, setEditCommentErrorUrl] = React.useState<string>('')
  const [editingCommentUrl, setEditingCommentUrl] = React.useState<string>('')
  const [editingCommentText, setEditingCommentText] = React.useState<string>('')

  React.useEffect(() => {
    setIsLoadingComments(true)
    get<Array<LogComment>>(`comments/?page_size=all&log=${props.logId}`)
      .then((data) => {
        setIsLoadingComments(false)
        setComments(data.data)
      })
      .catch(() => {
        setIsLoadingComments(false)
        setHasLoadCommentsError(true)
      })
  }, [props.logId])

  const saveComment = (): void => {
    setIsSavingComment(true)
    setHasSaveCommentError(false)

    const savePayload = {
      comment: commentText,
      log: props.logUrl,
      author: props.user?.url || null,
    }
    post<{ comment: string; log: string; author: string | null }, LogComment>('comments/', savePayload)
      .then((data) => {
        setIsSavingComment(false)
        setComments([...comments, data.data]) // add new comment to comment list
        setCommentText('')
      })
      .catch(() => {
        setIsSavingComment(false)
        setHasSaveCommentError(true)
      })
  }

  const updateComment = (): void => {
    setSaveEditCommentUrl(editingCommentUrl)

    const savePayload = {
      comment: editingCommentText,
    }
    patch<{ comment: string }, LogComment>(editingCommentUrl, savePayload)
      .then((data) => {
        setSaveEditCommentUrl('')
        setEditingCommentUrl('')
        setEditingCommentText('')
        setComments((prevComments) =>
          prevComments.map((x) => {
            return x.url === editingCommentUrl ? { ...x, comment: data.data.comment, modified: data.data.modified } : x
          })
        )
      })
      .catch(() => {
        setSaveEditCommentUrl('')
        setEditCommentErrorUrl(editingCommentUrl)
      })
  }

  const deleteComment = (commentUrl: string): void => {
    setSaveDeleteCommentUrl(commentUrl)
    setDeleteConfirmationCommentUrl('')

    remove(commentUrl)
      .then(() => {
        setSaveDeleteCommentUrl('')
        setComments((prevComments) => prevComments.filter((x) => x.url !== commentUrl))
      })
      .catch(() => {
        setDeleteCommentErrorUrl(commentUrl)
        setSaveDeleteCommentUrl('')
      })
  }

  const addCommentLabel =
    !isLoadingComments && comments.length === 0 ? t('logview.addfirstcomment') : t('logview.addcomment')

  return (
    <Stack spacing={1}>
      {isLoadingComments && <CircularProgress />}
      {deleteConfirmationCommentUrl && (
        <ConfirmationDialog
          header={t('logview.deletecomment')}
          body={t('logview.deletecommentconfirmation')}
          confirmButtonText={t('logview.deletecomment')}
          type="error"
          onConfirm={() => deleteComment(deleteConfirmationCommentUrl)}
          onClose={() => setDeleteConfirmationCommentUrl('')}
        />
      )}
      {comments.map((x) => {
        return (
          <Card key={x.url} variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {toReadableTimestamp(x.created)}
                </Typography>
                {x.created !== x.modified && (
                  <Tooltip title={toReadableTimestamp(x.modified)}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      {t('logview.editedcomment')}
                    </Typography>
                  </Tooltip>
                )}
              </Stack>
              {editingCommentUrl === x.url ? (
                <>
                  <CommentEditor
                    label={t('logview.editcomment')}
                    commentText={editingCommentText}
                    onChange={setEditingCommentText}
                  />
                  <LoadingButton variant="outlined" loading={saveEditCommentUrl === x.url} onClick={updateComment}>
                    {t('logview.savecomment')}
                  </LoadingButton>
                  <Button
                    disabled={saveEditCommentUrl === x.url}
                    onClick={() => {
                      setEditingCommentUrl('')
                      setEditingCommentText('')
                      setEditCommentErrorUrl('')
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  {editCommentErrorUrl === x.url && <Alert severity="error">{t('logview.updatecommenterror')}</Alert>}
                </>
              ) : (
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{x.comment}</Typography>
              )}
            </CardContent>
            {editingCommentUrl !== x.url && props.user && (x.author === props.user.url || props.user.is_staff) && (
              <CardActions>
                <Button
                  disabled={saveDeleteCommentUrl === x.url}
                  onClick={() => {
                    setEditingCommentUrl(x.url)
                    setEditingCommentText(x.comment)
                  }}
                >
                  {t('common.edit')}
                </Button>
                <LoadingButton
                  color="error"
                  loading={saveDeleteCommentUrl === x.url}
                  onClick={() => setDeleteConfirmationCommentUrl(x.url)}
                >
                  {t('common.delete')}
                </LoadingButton>
              </CardActions>
            )}
            {deleteCommentErrorUrl === x.url && <Alert severity="error">{t('logview.deletecommenterror')}</Alert>}
          </Card>
        )
      })}
      {hasLoadCommentsError && <Alert severity="error">{t('logview.loadcommentserror')}</Alert>}
      {hasSaveCommentError && <Alert severity="error">{t('logview.savecommenterror')}</Alert>}
      <CommentEditor label={addCommentLabel} commentText={commentText} onChange={setCommentText} />
      <LoadingButton variant="outlined" disabled={!commentText} loading={isSavingComment} onClick={saveComment}>
        {t('logview.savecomment')}
      </LoadingButton>
    </Stack>
  )
}

export default Comments
